import React, { useState, useCallback, useEffect } from "react";
import { Container, Row, Col, Modal } from "reactstrap";
import MetaTags from "react-meta-tags";
import Datatables from "pages/table/datatable";
import { tableColumns } from "./components/TableColumns";
import handleExportCSV from "pages/utils/handleExportCSV";
import { InputField, SelectField } from "pages/InputFields/InputFields";
import StatsCard from "./components/StatsCard";
import FilterBar from "./components/FilterBar";
import { showSuccessAlert, showErrorAlert } from "pages/utils/Alerts/alertMessages";
import { get, post } from "helpers/api_helper";

const Configurations = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("Add");
    const [selectedData, setSelectedData] = useState(null);
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    const [formData, setFormData] = useState({ host: "", port: "", user_name: "", password: "", encryption: null, from_email: "", status: "Active" });
    const [configurationsList, setConfigurationsList] = useState([]);

    const fetchConfigurations = useCallback(async () => {
        try {
            const res = await get("/superadmin/configurations");
            const data = res?.data || res;
            if (Array.isArray(data)) {
                setConfigurationsList(data);
            }
        } catch (e) {
            console.error("Failed to load configuration data from database", e);
        }
    }, []);

    useEffect(() => {
        fetchConfigurations();
    }, [fetchConfigurations]);

    const toggleModal = useCallback(() => {
        setModalOpen(!modalOpen);
        if (modalOpen) {
            setSelectedData(null);
            setFormData({ host: "", port: "", user_name: "", password: "", encryption: null, from_email: "", status: "Active" });
        }
    }, [modalOpen]);

    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        const { host, port, user_name, encryption, from_email, status } = formData;

        if (!host || !port || !user_name || !encryption || !from_email || !status) {
            showErrorAlert("Please fill the required fields.");
            return;
        }

        try {
            await post("/superadmin/configurations", formData);
            showSuccessAlert("Configuration data updated successfully in database!");
            setFormData({ host: "", port: "", user_name: "", password: "", encryption: null, from_email: "", status: "Active" });
            fetchConfigurations();
            toggleModal();
        } catch (e) {
            showErrorAlert("Failed to save configuration to database.");
        }
    };

    const handleAction = (mode, data = null) => {
        setModalMode(mode);
        setSelectedData(data);

        if ((mode === "Edit" || mode === "View") && data) {
            setFormData({ ...data, encryption: data.encryption ? { value: data.encryption, label: data.encryption } : null });
        } else {
            setFormData({ host: "", port: "", user_name: "", password: "", encryption: null, from_email: "", status: "Active" });
        }
        setModalOpen(true);
    };

    // --- Calculations ---
    const isAnySelected = selectedRowIds.length > 0;

    return (
        <React.Fragment>
            <div className="dash-page ms-4 me-4">
                <MetaTags>
                    <title>Configurations | {process.env.REACT_APP_TITLE}</title>
                </MetaTags>
                <Container fluid>
                    <div className="p-3">
                        <Row className="align-items-center mb-4">
                            <Col md="4">
                                <h4 className="mb-0 fw-bolder">Configurations</h4>
                            </Col>
                            <Col md="8" className="d-flex justify-content-end gap-2">
                                {isAnySelected && (
                                    <>
                                        <button className="btn btn-danger border px-3 animate__animated animate__fadeIn" style={{ minWidth: "130px" }}
                                            onClick={() => handleAction("Delete")}>
                                            <i className="bx bx-trash me-1"></i> Delete ({selectedRowIds.length})
                                        </button>
                                        <button className="btn btn-success border px-3 animate__animated animate__fadeIn" style={{ minWidth: "130px" }}
                                            onClick={handleExportCSV}>
                                            <i className="bx bx-export me-1"></i> Export All ({selectedRowIds.length})
                                        </button>
                                    </>
                                )}
                                <button className="btn btn-primary border px-3 animate__animated animate__fadeIn" style={{ minWidth: "130px" }}
                                    onClick={() => handleAction("Add")}>
                                    <i className="bx bx-plus me-1"></i> Add Configuration
                                </button>
                            </Col>
                        </Row>

                        <StatsCard configurationsList={configurationsList} />

                        <FilterBar />

                        <div className="p-3 bg-white shadow-sm border" style={{ borderRadius: "10px" }}>
                            <Datatables
                                columns={tableColumns(handleAction)}
                                showTableOnly={true}
                                rows={configurationsList}
                                keyField={"id"}
                                isCheckbox={true}
                                loading={false}
                                setSelectedRowIds={setSelectedRowIds}
                            />
                        </div>
                    </div>

                    <Modal isOpen={modalOpen} toggle={toggleModal} size="lg" className="custom-modal">
                        <div className="modal-header">
                            <h5 className="modal-title fw-bold mb-1">{`${modalMode} ${selectedData ? selectedData.host : 'Client'}`}</h5>
                            <button type="button" onClick={toggleModal} className="close" aria-label="Close"></button>
                        </div>

                        <div className="modal-body">
                            {modalMode === "View" && selectedData ? (
                                <>
                                    <div className="text-center p-3">
                                        <div className="avatar-md mx-auto mb-3">
                                            <span className="avatar-title rounded-circle bg-primary-subtle text-primary h2">
                                                {selectedData.host.charAt(0)}
                                            </span>
                                        </div>
                                        <h5 className="mb-1 fw-bolder">{selectedData.host}</h5>
                                        <p className="text-muted mt-2"><strong>Port:</strong> {selectedData.port}</p>
                                        <hr />
                                        <Row className="text-start">
                                            <Col xs="6" className="mb-2"><strong>User Name:</strong> {selectedData.user_name}</Col>
                                            <Col xs="6"><strong>Encryption:</strong> {selectedData.encryption}</Col>
                                        </Row>
                                        <Row className="text-start mt-2">
                                            <Col xs="6" className="mb-2"><strong>From Email:</strong> {selectedData.from_email}</Col>
                                            <Col xs="6"><strong>Status:</strong> {selectedData.status}</Col>
                                        </Row>
                                    </div>
                                    <hr />
                                    <div className="d-flex justify-content-end gap-2 mt-2">
                                        <button className="btn btn-danger" style={{ width: "100px" }} onClick={toggleModal}>Close</button>
                                    </div>
                                </>
                            ) : (
                                <div className="p-2">
                                    <Row className="g-3">
                                        <Col md="6">
                                            <InputField label="SMTP Host" name="host" type="text" value={formData.host} onChange={handleChange} />
                                        </Col>

                                        <Col md="6">
                                            <InputField label="Port" name="port" type="number" value={formData.port} onChange={handleChange} />
                                        </Col>

                                        <Col md="6">
                                            <InputField label="User Name" name="user_name" type="text" value={formData.user_name} onChange={handleChange} />
                                        </Col>

                                        <Col md="6">
                                            <InputField label="Password" name="password" type="password" value={formData.password} onChange={handleChange} />
                                        </Col>

                                        <Col md="6">
                                            <SelectField label="Encryption" value={formData.encryption} name="Encryption" isClearable={true} placeholder="Select Encryption"
                                                onChange={(option) => setFormData(prev => ({ ...prev, encryption: option || null }))}
                                                options={[
                                                    { value: "TLS", label: "TLS" },
                                                    { value: "SSL", label: "SSL" },
                                                    { value: "None", label: "None" },
                                                ]}
                                            />
                                        </Col>

                                        <Col md="6">
                                            <InputField label="From Email" name="from_email" type="text" value={formData.from_email} onChange={handleChange} />
                                        </Col>

                                    </Row>

                                    <div className="d-flex justify-content-end gap-2 mt-4">
                                        <button className="btn btn-danger" style={{ width: "100px" }} onClick={toggleModal}>Cancel</button>
                                        <button className="btn btn-primary px-4" onClick={handleSubmit}
                                            style={{ width: `${modalMode === "Add" ? "110px" : "140px"}` }}>
                                            {modalMode === "Add" ? "Create" : "Save Changes"}</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Modal>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default Configurations;